import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactModal({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We will get back to you shortly.");
      setForm({ name: "", email: "", message: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="contact-modal"
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div
            className="absolute inset-0 bg-white/70 backdrop-blur-md"
            onClick={() => onOpenChange(false)}
          />
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="relative w-full max-w-lg border border-[#D4AF37]/30 bg-white p-8 shadow-[0_40px_100px_rgba(15,23,42,0.15)] sm:p-12"
          >
            <button
              data-testid="contact-close-button"
              onClick={() => onOpenChange(false)}
              className="absolute right-6 top-6 text-slate-400 transition-colors hover:text-slate-900"
            >
              <X className="h-5 w-5" />
            </button>

            <p className="f-mono text-[10px] tracking-[0.4em] text-[#B8860B]">CONTACT</p>
            <h3 className="f-display mt-3 text-3xl font-light tracking-tight text-slate-900">
              Start a conversation.
            </h3>

            <form onSubmit={submit} className="mt-10 space-y-8">
              <div>
                <label className="f-mono block text-[10px] tracking-[0.3em] text-slate-500">
                  FULL NAME
                </label>
                <input
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  className="mt-2 w-full border-b border-slate-300 bg-transparent pb-3 text-base font-light text-slate-900 outline-none transition-colors focus:border-[#D4AF37]"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="f-mono block text-[10px] tracking-[0.3em] text-slate-500">
                  EMAIL ADDRESS
                </label>
                <input
                  data-testid="contact-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  className="mt-2 w-full border-b border-slate-300 bg-transparent pb-3 text-base font-light text-slate-900 outline-none transition-colors focus:border-[#D4AF37]"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="f-mono block text-[10px] tracking-[0.3em] text-slate-500">
                  MESSAGE
                </label>
                <textarea
                  data-testid="contact-message-input"
                  required
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  className="mt-2 w-full resize-none border-b border-slate-300 bg-transparent pb-3 text-base font-light text-slate-900 outline-none transition-colors focus:border-[#D4AF37]"
                  placeholder="How can we help?"
                />
              </div>
              <p className="text-xs font-light leading-relaxed text-slate-500">
                Alle Felder sind Pflichtfelder. Ihre Angaben (Name, E-Mail-Adresse, Nachricht)
                werden ausschlie&szlig;lich zur Bearbeitung Ihrer Anfrage verarbeitet und nicht
                zu Marketingzwecken an Dritte weitergegeben. Details:{" "}
                <a
                  href="#/datenschutz"
                  className="text-[#B8860B] underline underline-offset-2"
                >
                  Datenschutzerkl&auml;rung
                </a>
                .
              </p>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="f-mono w-full rounded-full bg-[#D4AF37] py-4 text-[11px] tracking-[0.35em] text-white transition-colors duration-500 hover:bg-[#B8860B] disabled:opacity-60"
              >
                {sending ? "SENDING..." : "SEND MESSAGE"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
